import React from "react";
import {
  Box,
  Button,
  CardMedia,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  bob,
  cloudLift,
  fanIn,
  floatOrbit,
  motionSafe,
  rise,
  shimmerSweep,
  softPulse,
  staggerIn,
  studioDialogBackdropSx,
  studioDialogPaperSx,
} from "./studioDialogMotion";

const ACCENT = "#dc2626";

/** Escena animada: miniatura que se va + papelera (mismo lenguaje que ficha/Drive). */
function DeleteHeroStage({ accent }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 112,
        mt: 1.35,
        mb: 0.25,
        borderRadius: 2.5,
        overflow: "hidden",
        bgcolor: alpha(accent, 0.07),
        border: "1px solid",
        borderColor: alpha(accent, 0.22),
        boxShadow: `inset 0 1px 0 ${alpha("#fff", 0.35)}`,
        animation: `${rise} 420ms cubic-bezier(0.22, 1, 0.36, 1) both`,
        ...motionSafe,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          width: 90,
          height: 90,
          borderRadius: "50%",
          left: -18,
          top: -28,
          bgcolor: alpha(accent, 0.14),
          filter: "blur(2px)",
          animation: `${softPulse} 3.6s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: 64,
          height: 64,
          borderRadius: "50%",
          right: -8,
          bottom: -18,
          bgcolor: alpha(accent, 0.1),
          animation: `${floatOrbit} 5s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            width: "42%",
            background: `linear-gradient(90deg, transparent, ${alpha(
              "#fff",
              0.4,
            )}, transparent)`,
            animation: `${shimmerSweep} 3.2s ease-in-out infinite`,
            animationDelay: "0.5s",
            ...motionSafe,
          }}
        />
      </Box>

      {/* Miniaturas del historial que “salen” */}
      {[-10, 4].map((rot, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: `${22 + i * 16}%`,
            top: 22 + i * 6,
            width: 48,
            height: 60,
            borderRadius: 1.5,
            bgcolor: i === 0 ? "#fff" : alpha(accent, 0.12),
            border: "1.5px solid",
            borderColor: alpha(accent, 0.32 + i * 0.08),
            boxShadow: `0 8px 16px ${alpha(accent, 0.14)}`,
            display: "grid",
            placeItems: "center",
            zIndex: i + 1,
            "--from-rot": `${rot - 14}deg`,
            "--to-rot": `${rot}deg`,
            animation: `${fanIn} 520ms cubic-bezier(0.22, 1, 0.36, 1) both, ${bob} ${
              2.2 + i * 0.2
            }s ease-in-out infinite`,
            animationDelay: `${0.1 + i * 0.1}s, ${0.45 + i * 0.1}s`,
            ...motionSafe,
          }}
        >
          <CollectionsOutlinedIcon
            sx={{ fontSize: 22, color: accent, opacity: 0.85 - i * 0.15 }}
          />
        </Box>
      ))}

      {/* Papelera */}
      <Box
        sx={{
          position: "absolute",
          right: "14%",
          top: 20,
          width: 52,
          height: 52,
          borderRadius: "50%",
          bgcolor: alpha(accent, 0.16),
          border: "1.5px solid",
          borderColor: alpha(accent, 0.42),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 8px 18px ${alpha(accent, 0.22)}`,
          animation: `${cloudLift} 2.5s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <DeleteOutlineIcon sx={{ fontSize: 26, color: accent }} />
      </Box>
    </Box>
  );
}

/**
 * Confirmación al quitar una miniatura del historial AI Designs.
 */
export default function ConfirmHistoryDeleteDialog({
  open,
  item,
  thumbSrc = null,
  busy = false,
  onClose,
  onConfirm,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const accent = ACCENT;
  const label =
    String(item?.filename || "").trim() ||
    (item?.mode === "sketch"
      ? "Sketch"
      : item?.mode === "ref"
        ? "Ref"
        : t("presentacionesSearch.newDesign.historyDeleteItem", {
            defaultValue: "esta versión",
          }));

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      keepMounted={false}
      transitionDuration={{ enter: 280, exit: 160 }}
      slotProps={{
        backdrop: { sx: studioDialogBackdropSx },
      }}
      BackdropProps={{ sx: studioDialogBackdropSx }}
      PaperProps={{
        sx: {
          ...studioDialogPaperSx,
          borderRadius: 3.5,
          borderColor: alpha(accent, 0.22),
          boxShadow: `0 28px 64px ${alpha(accent, 0.18)}, 0 8px 24px rgba(15,23,42,0.16)`,
          background: isDark
            ? `linear-gradient(165deg, ${alpha(accent, 0.1)} 0%, ${theme.palette.background.paper} 42%)`
            : `linear-gradient(165deg, ${alpha(accent, 0.07)} 0%, #fff 48%)`,
          animation: open
            ? `${rise} 420ms cubic-bezier(0.22, 1, 0.36, 1) both`
            : undefined,
          ...motionSafe,
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          px: 2.5,
          pt: 2.1,
          pb: 1.35,
          overflow: "hidden",
          borderBottom: "1px solid",
          borderColor: alpha(accent, 0.14),
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(ellipse at 12% 0%, ${alpha(
              accent,
              0.18,
            )} 0%, transparent 55%)`,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "relative",
            animation: `${staggerIn} 360ms cubic-bezier(0.22, 1, 0.36, 1) both`,
            ...motionSafe,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 900, lineHeight: 1.2, letterSpacing: -0.2 }}
          >
            {t("presentacionesSearch.newDesign.historyDeleteTitle", {
              defaultValue: "¿Quitar del historial?",
            })}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.55, lineHeight: 1.45 }}
          >
            {t("presentacionesSearch.newDesign.historyDeleteBody", {
              defaultValue:
                "Se elimina solo de este AI Design. La ficha o moodboard ya creados no se tocan.",
            })}
          </Typography>
        </Box>
        <DeleteHeroStage accent={accent} />
      </Box>

      <DialogContent sx={{ pt: 2, px: 2.5, pb: 0.5 }}>
        <Box
          sx={{
            borderRadius: 2.5,
            overflow: "hidden",
            border: `1px solid ${alpha(accent, 0.2)}`,
            bgcolor: isDark ? alpha("#fff", 0.04) : alpha("#000", 0.03),
            mb: 1.1,
            animation: `${rise} 380ms ease both`,
            ...motionSafe,
          }}
        >
          {thumbSrc ? (
            <CardMedia
              component="img"
              image={thumbSrc}
              alt={label}
              sx={{
                width: "100%",
                maxHeight: 180,
                objectFit: "contain",
                display: "block",
                bgcolor: isDark ? alpha("#fff", 0.03) : "#f8fafc",
              }}
            />
          ) : (
            <Box
              sx={{
                height: 120,
                display: "grid",
                placeItems: "center",
                color: accent,
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 36, opacity: 0.7 }} />
            </Box>
          )}
        </Box>
        {label ? (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              maxWidth: "100%",
              px: 1,
              py: 0.35,
              borderRadius: 999,
              bgcolor: alpha(accent, 0.08),
              border: "1px solid",
              borderColor: alpha(accent, 0.18),
              animation: `${staggerIn} 400ms ease both`,
              ...motionSafe,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Typography>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          pb: 2.25,
          pt: 1.5,
          gap: 1,
          animation: `${staggerIn} 440ms cubic-bezier(0.22, 1, 0.36, 1) 80ms both`,
          ...motionSafe,
        }}
      >
        <Button
          onClick={onClose}
          disabled={busy}
          color="inherit"
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          {t("common.cancel", { defaultValue: "Cancelar" })}
        </Button>
        <Button
          onClick={onConfirm}
          disabled={busy || !item}
          variant="contained"
          color="error"
          startIcon={<DeleteOutlineIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            boxShadow: `0 8px 20px ${alpha(accent, 0.32)}`,
          }}
        >
          {t("presentacionesSearch.newDesign.historyDeleteConfirm", {
            defaultValue: "Quitar",
          })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
